import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import * as Yup from 'yup';
import { useFormik } from 'formik';

export const Login = () => {
  const { t } = useTranslation();
  const { login, isLoading } = useAuth();

  const validationSchema = Yup.object({
    phone: Yup.string()
      .required(t('validation.required')),
    password: Yup.string()
      .required(t('validation.required'))
      .min(8, t('validation.minLength', { length: 8 })),
  });

  const formik = useFormik({
    initialValues: {
      phone: '',
      password: '',
    },
    validationSchema,
    onSubmit: (values) => {
      login.mutate({
        phone: values.phone,
        password: values.password,
        roles: ['ADMIN']
      });
    },
  });

  const getErrorMessage = (error: any) => {
    if (error.response?.data?.err?.params === 'INVALID_USERNAME_OR_PASSWORD') {
      return t('auth.login.error.invalidCredentials');
    }
    if (error.response?.status === 403) {
      return t('auth.login.error.accountBlocked');
    }
    if (error.response) {
      return t('auth.login.error.serverError');
    }
    return t('auth.login.error.networkError');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
        <div>
          <h2 className="text-center text-3xl font-bold text-gray-900">
            {t('auth.login.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.login.description')}
          </p>
        </div>
        {login.error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg text-sm">
            {getErrorMessage(login.error)}
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={formik.handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {t('auth.login.phone')}
              </label>
              <input
                id="phone"
                name="phone"
                type="text"
                required
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`input input-bordered w-full mt-1 ${
                  formik.touched.phone && formik.errors.phone ? 'input-error' : ''
                }`}
                placeholder={t('auth.login.phonePlaceholder')}
              />
              {formik.touched.phone && formik.errors.phone && (
                <div className="text-error text-sm mt-1">{formik.errors.phone}</div>
              )}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.login.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`input input-bordered w-full mt-1 ${
                  formik.touched.password && formik.errors.password ? 'input-error' : ''
                }`}
                placeholder={t('auth.login.passwordPlaceholder')}
              />
              {formik.touched.password && formik.errors.password && (
                <div className="text-error text-sm mt-1">{formik.errors.password}</div>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('auth.login.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 